import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { localize, translationKey } from '../i18n';
import Button from '../components/ui/FitButton';
import { installStoreUrl } from '../utils/installAttribution';
import { trackInstallEvent } from '../utils/installAnalytics';

function RepeatReminderDemo() {
    const [stage, setStage] = useState('first');
    useEffect(() => {
        if (stage !== 'dismissed') return undefined;
        let timer;
        const schedule = () => {
            clearTimeout(timer);
            if (!document.hidden) timer = setTimeout(() => setStage('again'), 1800);
        };
        document.addEventListener('visibilitychange', schedule);
        schedule();
        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', schedule);
        };
    }, [stage]);
    const taken = stage === 'taken';
    return <Box component="section" aria-labelledby="install-demo-title" sx={{ py: { xs: 4, md: 5 } }}>
        <Typography id="install-demo-title" component="h2" sx={{ fontSize: '1.3rem', fontWeight: 800, mb: 1 }}>
            {localize('알림을 꺼보세요. 다시 알려드릴게요.')}
        </Typography>
        <Typography sx={{ color: '#777', fontSize: '.9rem', mb: 2.5, lineHeight: 1.7 }}>
            {localize('알림 흐름 예시예요. 실제 알림이나 복용 기록은 만들지 않아요.')}
        </Typography>
        <Box sx={{ bgcolor: '#faf8f2', borderRadius: 4, p: { xs: 2.5, md: 4 }, minHeight: 260,
            display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box role="status" aria-live="polite" sx={{ mb: 2.5 }}>
                {taken ? <CheckCircleOutlineIcon sx={{ color: '#3b7652', mb: 1 }}
                    /> : <NotificationsActiveOutlinedIcon sx={{ color: '#8b711b', mb: 1 }} />}
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.5 }}>
                    {stage === 'first' && localize('약 먹을 시간이에요')}
                    {stage === 'dismissed' && localize('알림은 껐어요. 약은 아직이에요.')}
                    {stage === 'again' && localize('아 맞다, 약! 다시 알려드려요.')}
                    {taken && localize('복용 완료. 이번 알림은 여기까지예요.')}
                </Typography>
                <Typography sx={{ color: '#777', mt: .8, lineHeight: 1.7, fontSize: '.9rem' }}>
                    {stage === 'first' && localize('바쁠 때 알림을 넘겼다면 어떻게 될까요?')}
                    {stage === 'dismissed' && localize('설정한 반복 간격 뒤에…')}
                    {stage === 'again' && localize('한 번 놓쳤어도 다시 챙길 기회를 드려요.')}
                    {taken && localize('약을 먹고 복용 완료를 누르면 해당 회차의 반복이 멈춰요.')}
                </Typography>
            </Box>
            <Button variant="contained" fullWidth disabled={stage === 'dismissed'}
                onClick={() => setStage(taken ? 'first' : stage === 'first' ? 'dismissed' : 'taken')}
                sx={{ bgcolor: '#ffe566', color: '#5a4a00', boxShadow: 'none', borderRadius: 3,
                    minHeight: 48, '&:hover': { bgcolor: '#f0d657', boxShadow: 'none' } }}>
                {stage === 'first' && localize('알림 끄기')}
                {stage === 'dismissed' && localize('다시 알려드리는 중')}
                {stage === 'again' && localize('복용 완료')}
                {taken && localize('다시 보기')}
            </Button>
        </Box>
        <Typography sx={{ color: '#888', fontSize: '.8rem', lineHeight: 1.7, mt: 1.5 }}>
            {localize('예시는 짧게 재생돼요. 실제 반복 간격과 최대 알림 시간은 약마다 설정할 수 있어요.')}
        </Typography>
    </Box>;
}

export default function InstallGuidePage() {
    const { search } = useLocation();
    const lastView = useRef(null);
    useEffect(() => {
        if (lastView.current === search) return;
        lastView.current = search;
        void trackInstallEvent('install_landing_view', search);
    }, [search]);
    return <Box component="main" sx={{ bgcolor: '#fff', minHeight: '100svh', color: '#1a1a1a', overflowWrap: 'anywhere',
        px: 3, pt: 'calc(env(safe-area-inset-top, 0px) + 28px)',
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 28px)' }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
            <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 1.3, mb: 4 }}>
                <Box component="img" src="/icon.png" alt="" sx={{ width: 42, height: 42, borderRadius: '12px' }} />
                <Typography sx={{ fontWeight: 800 }}>{localize('집요한약알림')}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start' }}>
                <Box component="section" aria-labelledby="install-title" >
                    <Typography component="h1" id="install-title" sx={{ fontSize: '2rem',
                        fontWeight: 900, lineHeight: 1.35, letterSpacing: '-.03em', wordBreak: 'keep-all', mb: 2.5 }}>
                        {localize('약은 이따, 알림은 벌써 껐나요?')}
                    </Typography>
                    <Typography sx={{ color: '#666', lineHeight: 1.9, fontSize: '1.05rem', wordBreak: 'keep-all', mb: 2 }}>
                        {localize('알림을 꺼도 설정한 간격으로 다시 알려줘요. 바쁜 일이 끝난 뒤에도 약을 챙길 수 있도록요.')}
                    </Typography>
                    <Typography sx={{ lineHeight: 1.8, fontWeight: 700, mb: 3 }}>
                        {localize('회원가입 없이, 자꾸 놓치는 약 하나부터.')}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.2 }}>
                        {[['ios', AppleIcon, translationKey('App Store 설치')], ['android', AndroidIcon, translationKey('Google Play 설치')]].map(([store, Icon, label]) =>
                            <Button key={store} component="a" href={installStoreUrl(store, search)} variant="contained"
                                onClick={() => { void trackInstallEvent('install_store_click', search, store); }}
                                startIcon={<Icon />} fullWidth sx={{ minHeight: 52, borderRadius: 3, fontWeight: 800,
                                    bgcolor: store === 'ios' ? '#111827' : '#ffe566',
                                    color: store === 'ios' ? '#fff' : '#5a4a00', boxShadow: 'none',
                                    '&:hover': { bgcolor: store === 'ios' ? '#263144' : '#f0d657', boxShadow: 'none' } }}>
                                {localize(label)}
                            </Button>)}
                    </Box>
                    <Typography sx={{ color: '#888', fontSize: '.8rem', lineHeight: 1.8, mt: 1.5 }}>
                        {localize('무료 설치 · 앱 내 광고 · 선택형 광고 제거 구매')}
                    </Typography>
                </Box>
                <RepeatReminderDemo />
            </Box>
            <Box component="footer" sx={{ borderTop: '1px solid #eee', mt: { xs: 1, md: 5 }, pt: 3 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>{localize('약 하나 등록하고, 나에게 맞는 간격으로 시작하세요.')}</Typography>
                <Typography sx={{ color: '#777', lineHeight: 1.8, fontSize: '.9rem' }}>
                    {localize('복용 시간과 기록은 앱에서 관리해요. 이 페이지에서는 설치만 안내해요.')}
                </Typography>
                <Box component="nav" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Link href="/health-data" sx={{ py: 1, minHeight: 44, color: '#776000', overflowWrap: 'anywhere' }}>{localize('건강정보 처리 안내')}</Link>
                    <Link href="/privacy" sx={{ py: 1, minHeight: 44, color: '#776000', overflowWrap: 'anywhere' }}>{localize('개인정보 처리방침')}</Link>
                </Box>
            </Box>
        </Box>
    </Box>;
}
